import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import type { ExternalFilters } from '../types/ExternalFilters';
import type { Filters } from '../types/Filters';
import { unwrapFilterValuesRaw } from '../utils/unwrapFilterValuesRaw';
type UseGridFilterSyncParams = {
  filterValues: Filters;
  onExternalFilterChange: ((filters: ExternalFilters) => void) | undefined;
};

/**
 * Notifies the host whenever filterValues change, EXCLUDING the initial mount.
 *
 * Uses a prevRef pattern: skip when null (first run) or when reference is unchanged.
 * store.setFilterValues has a referential equality guard, so this fires only on actual mutations.
 */
export const useGridFilterSync = params => {
  const $ = _c(5);
  const onExternalFilterChangeRef = useRef(params.onExternalFilterChange);
  let t0;
  if ($[0] !== params.onExternalFilterChange) {
    t0 = () => {
      onExternalFilterChangeRef.current = params.onExternalFilterChange;
    };
    $[0] = params.onExternalFilterChange;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useEffect(t0);
  const prevFilterValuesForNotificationRef = useRef(null);
  let t1;
  let t2;
  if ($[2] !== params.filterValues) {
    t1 = () => {
      if (prevFilterValuesForNotificationRef.current === null) {
        prevFilterValuesForNotificationRef.current = params.filterValues;
        return;
      }
      if (prevFilterValuesForNotificationRef.current === params.filterValues) {
        return;
      }
      prevFilterValuesForNotificationRef.current = params.filterValues;
      onExternalFilterChangeRef.current?.(unwrapFilterValuesRaw(params.filterValues));
    };
    t2 = [params.filterValues];
    $[2] = params.filterValues;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
};